import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import * as React from "react";
import { createPortal } from "react-dom";
import * as ReactDOM from "react-dom/client";
import { i18n } from "../src/i18n";
import Alert from "../src/UI/Alerts/Alert";
import PaymentButton from "../src/UI/PaymentButton";
import { handleStripeConfirmError } from "./payments/handleStripeConfirmError";
import { Terms } from "./payments/Terms";
import { useTermsAgreed } from "./payments/useTermsAgreed";

document.addEventListener("turbo:load", init);
document.addEventListener("turbo:frame-load", init);
document.addEventListener("turbo:before-cache", resetMountedState);

const SUBMIT_FOOTER_ID = "payment-submit-footer";

function resetMountedState() {
  const node = document.querySelector("#pilotOrderFormStripeCheckout");

  if (!node) {
    return;
  }

  delete (node as HTMLElement).dataset.reactMounted;
}

function init(event) {
  const node = event.target.querySelector("#pilotOrderFormStripeCheckout");

  if (!node) {
    return;
  }

  if (node.dataset.reactMounted === "true") {
    return;
  }

  node.dataset.reactMounted = "true";

  // The portalled button sits outside the React root, so a restored Turbo snapshot carries a copy of it that the new
  // mount would render alongside.
  const submitFooter = document.getElementById(SUBMIT_FOOTER_ID);

  if (submitFooter) {
    submitFooter.innerHTML = "";
  }

  const stripePromise = loadStripe(node.dataset.stripePublishableKey);
  const clientSecret = node.dataset.stripeClientSecret;
  const returnUrl = node.dataset.returnUrl;
  const costPence = node.dataset.costPence;
  const options = { clientSecret };
  const root = ReactDOM.createRoot(node);

  root.render(
    <>
      <Elements options={options} stripe={stripePromise}>
        <CheckoutForm returnUrl={returnUrl} costPence={costPence} />
      </Elements>
    </>
  );
}

interface CheckoutFormProps {
  returnUrl: string;
  costPence: number;
}

function CheckoutForm({ returnUrl, costPence }: CheckoutFormProps) {
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const terms = useTermsAgreed();

  const stripe = useStripe();
  const elements = useElements();
  // Resolved before the first paint so the button never flashes inline before teleporting into the footer.
  const [submitFooter] = React.useState<HTMLElement | null>(() =>
    document.getElementById(SUBMIT_FOOTER_ID)
  );

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (!terms.allAgreed || !stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    });

    handleStripeConfirmError(error, setError);

    setIsLoading(false);
  };

  const submitButton = (
    <PaymentButton
      id="submit"
      form="payment-form"
      className={submitFooter ? "rounded-full" : "mb-4"}
      loading={isLoading || !stripe || !elements}
      disabled={!terms.allAgreed}
    >
      Pay {i18n.localize("currency", costPence / 100)}
    </PaymentButton>
  );

  return (
    <form id="payment-form" className="mb-4" onSubmit={handleSubmit}>
      <PaymentElement id="payment-element" />
      <div className="mb-4 mt-6">
        <Terms
          id="stripe"
          onTermsChange={terms.setConditions}
          onPolicyChange={terms.setPrivacyPolicy}
        />
      </div>

      {submitFooter ? createPortal(submitButton, submitFooter) : submitButton}

      {error && <Alert type="error">{error}</Alert>}
    </form>
  );
}
